import React, { useEffect, useRef, useState } from 'react';
import { Check, PenLine, UserRound } from 'lucide-react';
import SignaturePad from 'components/SignaturePad';
import { CAMPO_TEXTO } from './comun';

// Paso de firma y del inspector.
// - PIN: el nombre lo fija el ingreso; se muestra como texto, nunca editable.
//   El PIN ya identifica a la persona: en vez de firma dibujada, una casilla
//   "Confirmo que realicé esta inspección" (obligatoria).
// - SSO / kiosko: campo con la lista de guías de siempre y firma dibujada.
// La firma se guarda como Blob al terminar cada trazo. Si al volver al paso ya
// hay una firma, se muestra la imagen con "Firmar de nuevo" (el lienzo nuevo
// estaría vacío y confundiría).
export default function PasoFirma({ esPin, nombrePin, guia, onGuia, guias, firmaBlob, onFirma, confirmado = false, onConfirmar }) {
  const padRef = useRef(null);
  const turnoRef = useRef(0);
  const [verFirma, setVerFirma] = useState(() => !!firmaBlob);

  // La firma del borrador llega de IndexedDB después de montar: si el lienzo
  // sigue en blanco, se muestra la imagen guardada.
  useEffect(() => {
    if (firmaBlob && !verFirma && padRef.current?.isEmpty?.() !== false) setVerFirma(true);
    // Solo cuando cambia la firma; verFirma lo maneja "Firmar de nuevo".
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firmaBlob]);

  // La URL se crea y se revoca en el MISMO efecto: con useMemo + limpieza, el
  // doble montaje de StrictMode revocaba la URL que seguía en uso (imagen rota).
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (!verFirma || !firmaBlob || typeof URL === 'undefined' || !URL.createObjectURL) {
      setUrl('');
      return undefined;
    }
    const u = URL.createObjectURL(firmaBlob);
    setUrl(u);
    return () => URL.revokeObjectURL?.(u);
  }, [verFirma, firmaBlob]);

  // Cada aviso del panel lleva su turno: si llega un "borrar" mientras se lee el
  // Blob del trazo anterior, gana el más nuevo. Salir del paso NO invalida el
  // turno: quien firma y toca Siguiente al instante no debe perder la firma
  // (onFirma es del asistente, que sigue montado).
  const alTrazar = async () => {
    const mio = ++turnoRef.current;
    const blob = (await padRef.current?.getBlob()) ?? null;
    if (mio === turnoRef.current) onFirma(blob);
  };
  const alBorrar = () => {
    turnoRef.current += 1;
    onFirma(null);
  };
  const firmarDeNuevo = () => {
    turnoRef.current += 1;
    onFirma(null);
    setVerFirma(false);
  };

  return (
    <div>
      <h2 tabIndex={-1} className="text-2xl font-bold leading-tight text-foreground focus:outline-none">
        {esPin ? 'Confirmación' : 'Firma'}
      </h2>

      <div className="mt-4 rounded-2xl border border-border bg-card p-4 shadow-card">
        {esPin ? (
          <div className="flex items-center gap-3">
            <UserRound size={28} className="shrink-0 text-primary" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-muted-foreground">Inspector</p>
              {nombrePin ? (
                <p className="text-xl font-bold text-foreground">{nombrePin}</p>
              ) : (
                <p className="text-base font-medium text-foreground">
                  No se encontró el nombre del inspector. Salga y vuelva a entrar con su PIN.
                </p>
              )}
            </div>
          </div>
        ) : (
          <>
            <label htmlFor="inspector" className="block text-base font-semibold text-foreground">
              Nombre del inspector
            </label>
            <input
              id="inspector"
              list="guias"
              autoComplete="off"
              value={guia}
              onChange={(e) => onGuia(e.target.value)}
              className={`mt-2 min-h-[48px] ${CAMPO_TEXTO}`}
            />
            <datalist id="guias">
              {guias.map((g) => <option key={g} value={g} />)}
            </datalist>
          </>
        )}
      </div>

      {esPin ? (
        <label
          className={`mt-5 flex min-h-[64px] cursor-pointer items-center gap-4 rounded-2xl border-2 p-4 shadow-card ${
            confirmado ? 'border-primary bg-primary/5' : 'border-foreground/30 bg-white'
          }`}
        >
          <input
            type="checkbox"
            checked={confirmado}
            onChange={(e) => onConfirmar?.(e.target.checked)}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border-2 peer-focus-visible:ring-2 peer-focus-visible:ring-primary/40 ${
              confirmado ? 'border-primary bg-primary text-white' : 'border-foreground/40 bg-white'
            }`}
          >
            {confirmado ? <Check size={24} strokeWidth={3} /> : null}
          </span>
          <span className="text-lg font-bold leading-snug text-foreground">Confirmo que realicé esta inspección</span>
        </label>
      ) : (
        <>
          <p className="mt-5 flex items-center gap-2 text-base font-semibold text-foreground">
            <PenLine size={18} aria-hidden="true" />
            Firma del inspector
          </p>
          <div className="mt-2">
            {verFirma && firmaBlob ? (
              <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-card">
                <img src={url} alt="Firma registrada" className="h-48 w-full object-contain" />
                <div className="border-t border-border bg-muted/30 p-2">
                  <button
                    type="button"
                    onClick={firmarDeNuevo}
                    className="min-h-[48px] w-full rounded-xl border border-border bg-white px-4 text-base font-semibold text-foreground"
                  >
                    Firmar de nuevo
                  </button>
                </div>
              </div>
            ) : (
              <SignaturePad ref={padRef} onChange={alTrazar} onClear={alBorrar} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
